import { motion } from "framer-motion";
import SectionEyebrow from "./SectionEyebrow";

const steps = [
  {
    title: "Requirement Analysis",
    description:
      "At MK AI Technologies we believe that a successful project starts with a thorough understanding of your requirements. Our team conducts a detailed analysis of your project needs to ensure we have a clear understanding of your goals and objectives.",
  },
  {
    title: "Prototype Design",
    description:
      "Your vision begins to become a reality with an initial design shaped around your standards. We incorporate the revisions you need before moving forward with confidence.",
  },
  {
    title: "Development",
    description:
      "Every week, we keep you in the loop with updates on our progress. The approved design moves into development with quality and complete satisfaction as our priority.",
  },
  {
    title: "Final Product",
    description:
      "Your project comes to life through a polished launch. We bring your vision to fruition with a final result designed to exceed your expectations.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const viewport = { once: false, amount: 0.2 };

const timeline = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.85, ease: "easeOut", delay: 0.25 },
  },
};

const node = {
  hidden: { opacity: 0, scale: 0.72, y: 10 },
  visible: (index) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.38, ease: "easeOut", delay: 0.48 + index * 0.14 },
  }),
};

const stepContent = {
  hidden: { opacity: 0, y: 16 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut", delay: 0.64 + index * 0.14 },
  }),
};

export default function WhyUsParallax() {
  return (
    <section id="process" className="mk-process-surface border-y border-slate-200/80 py-24 lg:py-28">
      <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={stagger} className="mx-auto max-w-3xl text-center">
          <SectionEyebrow variants={reveal}>Our Approach</SectionEyebrow>
          <motion.h2 variants={reveal} className="mt-4 font-display text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl lg:text-5xl">From idea to launch</motion.h2>
          <motion.p variants={reveal} className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600">Four simple steps that take your project from the first conversation to a polished, working product.</motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={viewport} className="relative mt-14 hidden md:block">
         <motion.div variants={timeline} className="absolute left-[12.5%] right-[12.5%] top-[22px] h-px origin-left bg-[#8B4AFA]/55" />
          <div className="grid grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.article key={step.title} variants={stepContent} custom={index} className="group relative text-center">
              <motion.div variants={node} custom={index} className="relative z-10 mx-auto flex h-11 w-11 items-center justify-center rounded-full border-4 border-[#F5F3FF] bg-[#8B4AFA] text-xs font-bold text-white shadow-[0_0_0_1px_rgba(139,74,250,0.4)] transition-transform duration-200 group-hover:scale-110 group-hover:shadow-[0_0_0_5px_rgba(139,74,250,0.12)]">
                  {String(index + 1).padStart(2, "0")}
                </motion.div>
                <div className="mt-6">
                  <h3 className="font-display text-lg font-semibold text-slate-900 transition-colors duration-200 group-hover:text-[#7C3AED]">{step.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600 transition-colors duration-200 group-hover:text-slate-700">{step.description}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={viewport} className="relative mt-12 md:hidden">
          <motion.div variants={timeline} className="absolute bottom-5 left-5 top-5 w-px origin-top bg-[#8B4AFA]/55" />
          <div className="space-y-9">
            {steps.map((step, index) => (
              <motion.article key={step.title} variants={stepContent} custom={index} className="group relative grid grid-cols-[2.5rem_1fr] gap-5">
                <motion.div variants={node} custom={index} className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-4 border-[#F5F3FF] bg-[#8B4AFA] text-xs font-bold text-white shadow-[0_0_0_1px_rgba(139,74,250,0.4)] transition-transform duration-200 group-hover:scale-105 group-hover:shadow-[0_0_0_5px_rgba(139,74,250,0.12)]">
                  {String(index + 1).padStart(2, "0")}
                </motion.div>
                <div className="pb-1 pt-1">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Phase {index + 1}</span>
                   <p className="mt-1 font-display text-lg font-semibold text-slate-900 transition-colors duration-200 group-hover:text-[#7C3AED]">{step.title}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600 transition-colors duration-200 group-hover:text-slate-700">{step.description}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
